import React, { useEffect } from 'react'
import { Pencil, X } from 'lucide-react'
import Button from '../ui/button/Button'
import Tooltip from './Tooltip'

interface ModalProps {
    children: React.ReactNode
    title: string
    open: boolean
    handleClose: () => void
    handleSave?: () => void
    handleDelete?: () => void
    handleEdit?: () => void
    handleFilter?: () => void
    handleClean?: () => void
    canDelete?: boolean
    maxWidth?: string
}

const Modal = ({
    children,
    title,
    open,
    handleClose,
    handleSave,
    handleDelete,
    handleEdit,
    handleFilter,
    handleClean,
    canDelete,
    maxWidth = 'max-w-4xl'
}: ModalProps) => {
    const EditButton = () => {
        return (
            <div>
                <Tooltip label='Editar' placement='bottom'>
                    <button
                        onClick={handleEdit}
                        className='bg-brand-100 hover:bg-brand-200 dark:bg-brand-500/[0.12] dark:hover:bg-brand-500/[0.23] dark:text-brand-500 p-2 rounded-full'
                    >
                        <Pencil size={14} />
                    </button>
                </Tooltip>
            </div>
        )
    }

    // Esconde a barra de rolagem do corpo da página ao abrir Drawer (pra não ficar 2 barras de rolagem)
    useEffect(() => {
        if (open) {
            document.body.style.overflowY = 'hidden'
        } else {
            document.body.style.overflowY = 'auto'
        }

        return () => {
            document.body.style.overflowY = 'auto'
        }
    }, [open])

    if (!open) return null

    return (
        <main
            className={`fixed right-0 inset-0 z-40 transform transition-all ease-in-out duration-1000 ${
                open ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'
            }`}
        >
            {/* Background overlay */}
            <section
                className={`fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity ${
                    open ? 'translate-x-0 right-0' : '-translate-x-full -right-full'
                }`}
                onClick={handleClose}
            ></section>

            {/* Drawer content */}
            <section
                className={`w-screen top-14 left-0 right-0 mx-auto absolute delay-400 duration-500 ease-in-out transition-transform transform md:rounded-l-2xl border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-800 p-5 lg:p-6 ${maxWidth} ${
                    open ? 'translate-x-0' : 'translate-x-full'
                }`}
            >
                <article className='flex h-full flex-col'>
                    {/* Header */}
                    <header className='p-4 font-bold text-lg flex justify-between items-center text-gray-800 dark:text-white/90'>
                        <div className='flex items-center gap-2'>
                            <h2>{title}</h2>
                            {/* Edita */}
                            {handleEdit && <EditButton />}
                        </div>
                        <button
                            onClick={handleClose}
                            className='text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                        >
                            <X size={24} />
                        </button>
                    </header>

                    {/* Conteúdo rolável */}
                    <div className='flex-1 overflow-y-auto px-4 py-2'>{children}</div>

                    {/* Rodapé fixo */}
                    {(handleSave || handleFilter || handleDelete) && (
                        <footer className='p-4 text-right'>
                            <div className='space-x-4'>
                                {handleSave && (
                                    <>
                                        <Button variant='link' onClick={handleClose}>
                                            Fechar
                                        </Button>
                                        <Button variant='primary' onClick={handleSave}>
                                            Salvar
                                        </Button>
                                    </>
                                )}
                                {handleFilter && (
                                    <>
                                        <Button variant='link' onClick={handleClean}>
                                            Limpar
                                        </Button>
                                        <Button variant='primary' onClick={handleFilter}>
                                            Aplicar filtros
                                        </Button>
                                    </>
                                )}
                                {handleDelete && (
                                    <>
                                        <Button variant='link' onClick={handleClose}>
                                            Fechar
                                        </Button>
                                        <Button
                                            variant='primary'
                                            onClick={handleDelete}
                                            disabled={!canDelete}
                                            className='bg-error-400 hover:bg-error-500 [&:disabled]:bg-error-300'
                                        >
                                            Excluir
                                        </Button>
                                    </>
                                )}
                            </div>
                        </footer>
                    )}
                </article>
            </section>
        </main>
    )
}

export default Modal
